// components/admin/accounting/modals/DepositadoModal.jsx
import React from 'react';
import { ArrowDownLeft, ArrowUpRight, X } from 'lucide-react';

function DepositadoModal({
  isOpen,
  amount,
  onAmountChange,
  direction,
  onDirectionChange,
  method,
  onMethodChange,
  notes,
  onNotesChange,
  payment,
  onSave,
  onCancel,
  isSaving = false
}) {
  function formatCurrency(value) {
    if (value === null || value === undefined || value === '') return 'S/ 0.00';
    return `S/ ${Number(value || 0).toFixed(2)}`;
  }

  if (!isOpen) return null;

  const isFastMaxPayout = direction === 'fastmax_to_participant';
  const title = isFastMaxPayout
    ? 'Registrar deposito Fast Max -> usuario'
    : 'Registrar pago usuario -> Fast Max';
  const remaining = Number(payment?.remaining_balance ?? payment?.net_balance ?? 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 shadow-2xl max-w-lg w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-xs font-montserrat font-semibold text-slate-500 uppercase tracking-wider">
              Movimiento de dinero
            </p>
            <h3 className="text-xl font-bold text-slate-800 font-montserrat">
              {title}
            </h3>
          </div>
          <button
            onClick={onCancel}
            className="text-slate-400 hover:text-slate-600 transition-colors"
            disabled={isSaving}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {payment && (
          <div className="grid grid-cols-3 gap-2 mb-5">
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-[10px] font-bold text-slate-500 uppercase">Base</p>
              <p className="text-sm font-bold text-slate-800">{formatCurrency(payment.base_balance ?? payment.depositar)}</p>
            </div>
            <div className="bg-emerald-50 rounded-lg p-3">
              <p className="text-[10px] font-bold text-emerald-700 uppercase">FM -> Usuario</p>
              <p className="text-sm font-bold text-emerald-700">{formatCurrency(payment.fastmax_paid_out)}</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-3">
              <p className="text-[10px] font-bold text-blue-700 uppercase">Usuario -> FM</p>
              <p className="text-sm font-bold text-blue-700">{formatCurrency(payment.participant_paid_in)}</p>
            </div>
          </div>
        )}

        <div className="mb-5 rounded-lg border border-slate-200 p-3">
          <p className="text-xs font-semibold text-slate-600 font-montserrat mb-2">
            Direccion del movimiento
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => onDirectionChange('fastmax_to_participant')}
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left transition-all ${
                isFastMaxPayout
                  ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                  : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
              }`}
              disabled={isSaving}
            >
              <ArrowUpRight className="w-4 h-4" />
              <span className="text-sm font-montserrat font-semibold">Fast Max -> Usuario</span>
            </button>
            <button
              type="button"
              onClick={() => onDirectionChange('participant_to_fastmax')}
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left transition-all ${
                !isFastMaxPayout
                  ? 'border-blue-300 bg-blue-50 text-blue-800'
                  : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
              }`}
              disabled={isSaving}
            >
              <ArrowDownLeft className="w-4 h-4" />
              <span className="text-sm font-montserrat font-semibold">Usuario -> Fast Max</span>
            </button>
          </div>
        </div>

        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2 font-montserrat">
              Monto (S/)
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => onAmountChange(e.target.value)}
              step="0.01"
              min="0"
              placeholder="0.00"
              className="w-full px-4 py-3 text-lg border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent font-montserrat"
              autoFocus
              disabled={isSaving}
            />
            <p className="text-sm text-slate-500 mt-2 font-montserrat">
              Saldo actual: {formatCurrency(Math.abs(remaining))}{' '}
              {remaining > 0 ? 'por devolver' : remaining < 0 ? 'por cobrar' : 'cuadrado'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2 font-montserrat">
              Metodo / referencia
            </label>
            <input
              type="text"
              value={method}
              onChange={(e) => onMethodChange(e.target.value)}
              placeholder="transferencia, yape, plin, efectivo..."
              className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary font-montserrat text-sm"
              disabled={isSaving}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2 font-montserrat">
              Nota interna
            </label>
            <textarea
              value={notes}
              onChange={(e) => onNotesChange(e.target.value)}
              rows={3}
              placeholder="Ej: tienda pago la tarifa por adelantado, deposito parcial, comprobante en WhatsApp..."
              className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary font-montserrat text-sm resize-none"
              disabled={isSaving}
            />
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={isSaving}
            className="flex-1 px-4 py-3 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-all font-montserrat font-semibold disabled:opacity-60"
          >
            Cancelar
          </button>
          <button
            onClick={onSave}
            disabled={isSaving}
            className="flex-1 px-4 py-3 bg-brand-green text-white rounded-lg hover:bg-brand-green/90 transition-all font-montserrat font-semibold disabled:opacity-60"
          >
            {isSaving ? 'Guardando...' : 'Registrar movimiento'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DepositadoModal;
